import { createContext, useContext, useState } from "react";
import cx from "../../../lib/cx.js";
import styles from "./tabs.css";

/**
 * Context for managing and sharing states related to navigation tabs and theme settings.
 *
 * Provides the following properties:
 * - `activeTab` (string): Represents the currently active tab's identifier.
 * - `setActiveTab` (function | null): A setter function to update the active tab. Defaults to null.
 * - `onChange` (function | null): A callback function triggered when there is a change in the active tab. Defaults to null.
 * - `theme` (string): Specifies the current theme, defaulting to "light".
 *
 * This context is intended to be used for handling tab navigation and theme configurations across components.
 */
const Context = createContext({
  activeTab: "",
  setActiveTab: null,
  onChange: null,
  theme: "light",
});

/**
 * Represents a navigation item in a tab-based navigation component.
 *
 * @param {Object} props - The properties for the tab navigation item.
 * @param {string} props.id - The unique identifier for the navigation tab.
 * @param {string} props.title - The text label for the navigation tab.
 * @return {JSX.Element} A button element representing the navigation tab.
 */
function TabNavItem({ id, title }) {
  const { activeTab, setActiveTab, onChange, theme } = useContext(Context);
  const active = id === activeTab;

  function handleClick(e) {
    e.preventDefault();

    setActiveTab(id);

    if (onChange && !active) onChange(e);
  }

  return (
    <button
      className={cx(styles.navItem[theme], active && "active")}
      onClick={handleClick}
    >
      {title}
    </button>
  );
}

/**
 * Renders the content of the tab if it matches the active tab.
 *
 * @param {Object} props - The props for the component.
 * @param {string} props.id - The unique identifier of the tab.
 * @param {React.ReactNode} props.children - The content to display if the tab is active.
 * @return {React.ReactNode|null} The children elements if the tab is active, otherwise null.
 */
function TabContent({ id, children }) {
  const { activeTab } = useContext(Context);

  return id === activeTab ? children : null;
}

/**
 * A functional React component that renders a navigation bar using a theme from context.
 *
 * @param {Object} props - The props object passed to the TabNav component.
 * @param {React.ReactNode} props.children - The child elements to be displayed within the navigation bar.
 * @return {JSX.Element} The rendered navigation bar component.
 */
function TabNav({ children }) {
  const { theme } = useContext(Context);

  return <ul className={styles.nav[theme]}>{children}</ul>;
}

/**
 * A functional component that provides a tabbing interface with the ability to manage active tabs and custom themes.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.defaultTabId - The ID of the tab to be active by default.
 * @param {function} props.onChange - Callback function triggered when the active tab changes.
 * @param {React.ReactNode} props.children - The child components or elements to be rendered inside the Tabs.
 * @param {boolean} [props.w100=false] - A boolean flag to define if the Tabs should take 100% of the width.
 * @param {"light"|"dark"|"colorful"} [props.theme="light"] - The theme to be applied to the Tabs, can have values like "light" or other custom themes.
 * @return {JSX.Element} The rendered Tabs component.
 */
function Tabs({
  defaultTabId,
  onChange,
  children,
  w100 = false,
  theme = "light",
}) {
  const [activeTab, setActiveTab] = useState(defaultTabId);

  const contextValues = {
    activeTab,
    setActiveTab,
    onChange,
    theme,
  };

  return (
    <Context.Provider value={contextValues}>
      <div style={{ width: w100 ? "100%" : "" }}>{children}</div>
    </Context.Provider>
  );
}

export { Tabs as default, TabNav, TabNavItem, TabContent };
